import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Reviews from "@mui/icons-material/Reviews";
import { FullScreenDialog } from "../core/FullScreenDialog";
import { useQueryClient } from "@tanstack/react-query";
import Markdown from "react-markdown";
import {
  GameSellsheets,
  useGameSellsheets,
  useMyEntitlements,
  useRequestSellSheetFeedback,
} from "@seabrookstudios/pitch2table";
import Alert from "@mui/material/Alert";
import { AlwaysOnAppLink } from "../styleguide/AppLink";
import { Path } from "../Path";

/** Must match backend `SELL_SHEET_FEEDBACK_IN_PROGRESS`. */
export const SELLSHEET_FEEDBACK_IN_PROGRESS = "In Progress";
const feedbackDialogOpenByKey = new Map();

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").AttachmentId} props.attachmentId
 * @param {import("@seabrookstudios/pitch2table-core").SellSheet | undefined} props.sellSheet
 */
const SellSheetFeedback = ({ gameId, attachmentId, sellSheet }) => {
  const queryClient = useQueryClient();
  useRequestSellSheetFeedback(gameId, attachmentId);

  useEffect(() => {
    if (sellSheet?.feedback && sellSheet.feedback !== SELLSHEET_FEEDBACK_IN_PROGRESS) {
      return undefined;
    }

    const id = window.setInterval(() => {
      queryClient.invalidateQueries({ queryKey: GameSellsheets(gameId) });
    }, 10000);
    return () => window.clearInterval(id);
  }, [sellSheet?.feedback, gameId, queryClient]);

  if (!sellSheet?.feedback || sellSheet.feedback === SELLSHEET_FEEDBACK_IN_PROGRESS) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <Typography>
          Please wait while we gather feedback. This page will automatically refresh when the feedback is ready.
        </Typography>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Markdown>{sellSheet.feedback}</Markdown>
    </Box>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").AttachmentId} props.attachmentId
 * @param {import("@seabrookstudios/pitch2table-core").SellSheet} [props.sellsheet] Last row from parent while sellsheets refetch (avoids closing fullscreen modal)
 */
export const SellsheetFeedbackDialog = ({ gameId, attachmentId, sellsheet: sellSheetFromParent }) => {
  const { data: entitlements } = useMyEntitlements();
  const { data: sellSheets = [] } = useGameSellsheets(gameId);
  const sellSheetLive = sellSheets.find((s) => s.id === attachmentId);
  const sellSheet = sellSheetLive ?? sellSheetFromParent;
  const dialogKey = useMemo(() => `${gameId ?? ""}:${attachmentId ?? ""}`, [gameId, attachmentId]);
  const [isOpen, setIsOpen] = useState(() => feedbackDialogOpenByKey.get(dialogKey) === true);

  const canRequest = Boolean(entitlements?.hasSKU0004 || entitlements?.hasSKU0005);

  useEffect(() => {
    setIsOpen(feedbackDialogOpenByKey.get(dialogKey) === true);
  }, [dialogKey]);

  const openDialog = () => {
    feedbackDialogOpenByKey.set(dialogKey, true);
    setIsOpen(true);
  };

  const closeDialog = () => {
    feedbackDialogOpenByKey.set(dialogKey, false);
    setIsOpen(false);
  };

  const Trigger = (
    <Tooltip title="Get feedback on this sellsheet">
      <span>
        <IconButton onClick={openDialog}>
          <Reviews />
        </IconButton>
      </span>
    </Tooltip>
  );

  let body = <SellSheetFeedback gameId={gameId} attachmentId={attachmentId} sellSheet={sellSheet} />;

  if (sellSheet?.deleted) {
    body = <Alert severity="warning">Feedback is not available for deleted sell sheets.</Alert>;
  }

  if (!sellSheet?.deleted && (!entitlements || !canRequest)) {
    body = (
      <Alert severity="warning">
        A premium subscription is required to request feedback on sell sheets.{" "}
        <AlwaysOnAppLink to={Path.Premium}>Upgrade to enable this feature.</AlwaysOnAppLink>
      </Alert>
    );
  }

  return (
    <>
      {Trigger}
      <FullScreenDialog title="Sellsheet feedback" open={isOpen} onClose={closeDialog}>
        {body}
      </FullScreenDialog>
    </>
  );
};
